import messages from './Messages';
import { isANumber, convertNumbersToProperFormat } from '../../../utils/number';
import notify from '../../../components/Notify';
import { ITasks, IUsers } from 'store/store';
import { IAssignmentView, ITask } from 'store/tasks/tasksType';
import { smartDateString } from '../../../utils/date';
import { IUser } from '../../../store/users/usersType';

function getAssigneeString(
  assignee: { [p: number]: IAssignmentView },
  members: IUsers['map']
) {
  const users = Object.values(assignee).reduce(
    (current: { [key: string]: IUser[] }, assigneeOrFollower: any) => {
      const user = members[assigneeOrFollower.userId];
      if (user) {
        const group = assigneeOrFollower.properties?.roleName || '';
        if (!current[group]) current[group] = [];
        current[group].push(user);
      }
      return current;
    },
    {} as { [key: string]: IUser[] }
  );

  return Object.values(users).length > 0
    ? Object.entries(users)
        .map((userGroup: [string, IUser[]]) => {
          const groupName = userGroup[0];
          const groupContent = userGroup[1];
          if (groupContent.length < 5 || !groupName)
            return groupContent
              .map((user, index) =>
                messages.getAssignmentName(
                  user.name,
                  index + 1 !== groupContent.length
                )
              )
              .join(', ');
          return (
            convertNumbersToProperFormat(groupContent.length) + ' ' + groupName
          );
        })
        .join(', ')
    : '-';
}

export function getTaskListAsHtml(
  filteredTasks: ITasks['data'],
  users: IUsers['map'],
  includeNotStarted: boolean,
  includeComplete: boolean,
  includeStarted: boolean,
  companyId: number,
  title?: string
) {
  const assigneeTitle = Object.values(filteredTasks)[0].assigneeTitle!;
  const mainProgressTitle = Object.values(filteredTasks)[0].mainProgressTitle!;
  const printableFields = {};
  Object.values(filteredTasks).forEach((task) => {
    Object.values(task.fields)?.forEach((field) => {
      if (field.showInPrint) {
        printableFields[field.name] = field.title || field.name;
      }
    });
    if (task.haveWeight) {
      printableFields['$$weight$$'] = messages.taskWeight;
    }
    if (task.haveDueDate) {
      printableFields['$$dueDate$$'] = task.dueDateTitle ?? messages.dueDate;
    }
  });
  // todo when pasting in ck editor table we should make max to 100%
  let result = `<table style='max-width: calc(100vw - 200px)'><thead>${title ? `<th>${title}</th>` : ''}<tr><th>${messages.title}</th><th>${
    mainProgressTitle ? mainProgressTitle : messages.progressValue
  }</th><th>${messages.assignee(assigneeTitle)}</th>${Object.entries(
    printableFields
  ).reduce((current, item) => {
    return current + `<th>${item[1]}</th>`;
  }, '')}</tr></thead><tbody>`;

  const tableHead = result;
  let inExtraTable = false;

  Object.values(filteredTasks)
    .sort((it) => (it?.dueDate as unknown as number) || Number.MAX_VALUE)
    .forEach((value) => {
      const progress = value.mainProgress;
      const assigne: { [p: number]: IAssignmentView } = value.assignee;
      const assigneeStr = getAssigneeString(assigne, users);
      // const assigneeStr = Array.from(Object.values(assigne)).reduce(
      //   (current: string, value: any) => {
      //     return current + (current ? '، ' : '') + users[value.userId].name;
      //   },
      //   ''
      // );
      const dueDateInFuture = Number(value.dueDate) > new Date().getTime();
      if (dueDateInFuture && progress === 0 && !inExtraTable) {
        inExtraTable = true;
        result += `</tbody></table> ${messages.dueDateInFuture}` + tableHead;
      }
      if (progress === 0 && !includeNotStarted) return;

      if (progress === 100 && !includeComplete) return;
      const fieldsPart = Object.keys(printableFields).reduce(
        (current, item) => {
          const fieldValue =
            item === '$$weight$$'
              ? convertNumbersToProperFormat(value.weight, 1)
              : item === '$$dueDate$$'
                ? smartDateString(
                    value.dueDate,
                    false,
                    true,
                    true,
                    'jD jMMMM jYYYY'
                  )
                : value?.fields?.find(
                    (it) => it.name === item && it.showInPrint
                  )?.value || '';
          return current + `<td>${fieldValue}</td>`;
        },
        ''
      );
      if (progress! > 0 && progress! < 100 && !includeStarted) return;
      result =
        result +
        `<tr><td><a href="${getTaskLink(value, companyId)}" >${value.title} </a> </td><td> ${
          isANumber(progress)
            ? convertNumbersToProperFormat(progress!, 1) + '%'
            : ''
        } </td><td>  ${assigneeStr ? assigneeStr : ''} </td>${fieldsPart}</tr>`;
    });
  result = '<html><body>' + result + '</tbody></table></body></html>';
  return result;
}

export function copyTaskListToClipboard(
  filteredTasks: ITasks['data'],
  users: IUsers['map'],
  companyId: number,
  includeComplete: boolean,
  includeStarted: boolean,
  includeNotStarted: boolean,
  title?: string
) {
  if (Object.keys(filteredTasks).length === 0) return;
  const result = getTaskListAsHtml(
    filteredTasks,
    users,
    includeNotStarted,
    includeComplete,
    includeStarted,
    companyId,
    title
  );
  setClipboard(result, 'text/html');
  notify(messages.taskListCopied, 'successful');
}

export function getTaskLink(task: ITask, companyId: number) {
  const projectPart = `project/${task.projectId}/`;
  return `/t/${companyId}/${projectPart}task/editor/${task.id}/info`;
}

export function setClipboard(text: string, type: string = 'text/plain') {
  if (!window.navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  const blob = new Blob([text], { type });
  const data = [new ClipboardItem({ [type]: blob })];
  window.navigator.clipboard.write(data).then(
    function () {
      // console.log('Async: Copying to clipboard was successful!');
    },
    function (err) {
      console.error('Async: Could not copy text: ', err);
    }
  );
}

function fallbackCopyTextToClipboard(value: string) {
  const tempInput = document.createElement('div');
  tempInput.innerHTML = value;
  document.body.appendChild(tempInput);

  const range = document.createRange();
  range.selectNode(tempInput);
  window.getSelection()!.removeAllRanges(); // clear current selection
  window.getSelection()!.addRange(range); // to select text
  document.execCommand('copy');
  window.getSelection()!.removeAllRanges(); // to deselect
  document.body.removeChild(tempInput);
}
